import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  MagnifyingGlass,
  Plus,
  Phone,
  CalendarBlank,
  ArrowRight,
  Scissors,
  ArrowLeft,
  Warning,
  UploadSimple,
  CaretLeft,
  CaretRight,
} from '@phosphor-icons/react';
import { API, STATUS_CONFIG, formatCurrency, formatDateRelative, formatPhone } from './constants';
import { ClienteFormModal } from './ClienteForm';

const PAGE_SIZE = 50;

export const ClientesList = () => {
  const navigate = useNavigate();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editCliente, setEditCliente] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [resumo, setResumo] = useState({ novas: 0, recorrentes: 0, inativas: 0, total: 0 });
  const [error, setError] = useState('');
  const debounceRef = useRef(null);

  const fetchPage = useCallback(async (targetPage, buscaValue, statusValue) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        page: String(targetPage),
        page_size: String(PAGE_SIZE),
      });
      if (buscaValue) params.append('busca', buscaValue);
      if (statusValue) params.append('status', statusValue);
      const res = await fetch(`${API}/clientes/paginado?${params}`);
      if (!res.ok) throw new Error('Falha ao carregar clientes');
      const data = await res.json();
      setClientes(data.items || []);
      setPage(data.page || 1);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 0);
    } catch (err) {
      setClientes([]);
      setError('Nao foi possivel carregar. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchResumo = useCallback(async () => {
    try {
      const res = await fetch(`${API}/clientes/resumo`);
      if (res.ok) setResumo(await res.json());
    } catch { /* silencioso */ }
  }, []);

  // Load resumo once on mount
  useEffect(() => { fetchResumo(); }, [fetchResumo]);

  // Debounced fetch when busca or statusFilter changes -> volta para pagina 1
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchPage(1, busca.trim(), statusFilter);
    }, 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [busca, statusFilter, fetchPage]);

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditCliente(null);
    fetchPage(page, busca.trim(), statusFilter);
    fetchResumo();
  };

  const goToPage = (p) => {
    if (p < 1 || (totalPages && p > totalPages)) return;
    fetchPage(p, busca.trim(), statusFilter);
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Cards refletem a base completa; o filtro por status agora e server-side.
  const statusCounts = {
    novo: resumo.novas,
    recorrente: resumo.recorrentes,
    inativo: resumo.inativas,
  };
  const clientesVisiveis = clientes;

  const rangeInicio = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeFim = total === 0 ? 0 : Math.min(page * PAGE_SIZE, total);

  return (
    <div className="min-h-screen bg-[#F7F6F4]" data-testid="clientes-page">
      <div className="salonup-container">
        {/* Header */}
        <header className="salonup-header animate-fade-in">
          <div className="salonup-brand">
            <div className="salonup-logo-mark">
              <Scissors size={22} weight="bold" />
            </div>
            <span className="salonup-logo-text">Salon<span>Up</span></span>
          </div>
          <button
            onClick={() => navigate('/gestao')}
            data-testid="btn-back"
            className="salonup-btn-back"
          >
            <ArrowLeft size={18} weight="bold" />
            <span>Voltar para Gestao</span>
          </button>
        </header>

        {/* Breadcrumb */}
        <div className="mb-4 animate-fade-in opacity-0 delay-100" style={{ animationFillMode: 'forwards' }}>
          <p className="salonup-text-xs">
            <span className="cursor-pointer hover:text-[#1B4332] transition-colors" onClick={() => navigate('/')}>Home</span>
            <span className="mx-2">&middot;</span>
            <span className="cursor-pointer hover:text-[#1B4332] transition-colors" onClick={() => navigate('/gestao')}>Gestao</span>
            <span className="mx-2">&middot;</span>
            <span className="text-[#18181B] font-medium">Clientes</span>
          </p>
        </div>

        {/* Title Section */}
        <section className="mb-8 animate-fade-in-up opacity-0 delay-150" style={{ animationFillMode: 'forwards' }}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#1B433215' }}>
                <Users size={28} weight="regular" style={{ color: '#1B4332' }} />
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="salonup-heading-lg">Clientes</h1>
                  <span className="salonup-badge">FICHA VIVA</span>
                </div>
                <p className="salonup-text-sm mt-1">Cadastro, historico e vinculo com o cliente do salao</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="btn-importar"
                onClick={() => navigate('/gestao/clientes/importar')}
                className="inline-flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium border border-[#E4E4E7] text-[#71717A] hover:bg-white hover:text-[#18181B] transition-all"
              >
                <UploadSimple size={16} weight="bold" />
                <span>Importar</span>
              </button>
              <button
                data-testid="btn-novo-cliente"
                onClick={() => { setEditCliente(null); setShowForm(true); }}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium text-white transition-all hover:shadow-lg active:scale-[0.98]"
                style={{ backgroundColor: '#1B4332' }}
              >
                <Plus size={18} weight="bold" />
                <span>Nova Cliente</span>
              </button>
            </div>
          </div>
        </section>

        {/* Status Summary Cards */}
        <section className="grid grid-cols-3 gap-3 mb-6 animate-fade-in-up opacity-0 delay-200" style={{ animationFillMode: 'forwards' }}>
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
            <button
              key={key}
              data-testid={`filter-${key}`}
              onClick={() => setStatusFilter(statusFilter === key ? '' : key)}
              className={`p-4 rounded-2xl border text-left transition-all ${
                statusFilter === key
                  ? 'ring-2 ring-offset-1 shadow-sm'
                  : 'hover:shadow-sm'
              }`}
              style={{
                backgroundColor: statusFilter === key ? cfg.bg : '#fff',
                borderColor: statusFilter === key ? cfg.color : '#E4E4E7',
                ringColor: cfg.color,
              }}
            >
              <span className="text-2xl font-semibold" style={{ color: cfg.color, fontFamily: 'Outfit' }}>
                {statusCounts[key]}
              </span>
              <p className="text-xs mt-1 font-medium" style={{ color: cfg.color }}>
                {cfg.label}{statusCounts[key] !== 1 ? 's' : ''}
              </p>
            </button>
          ))}
        </section>

        {/* Search */}
        <section className="mb-6 animate-fade-in-up opacity-0 delay-250" style={{ animationFillMode: 'forwards', animationDelay: '250ms' }}>
          <div className="relative">
            <MagnifyingGlass
              size={20}
              weight="regular"
              className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A1A1AA]"
            />
            <input
              data-testid="search-clientes"
              type="text"
              placeholder="Buscar por nome, telefone ou codigo..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              className="w-full pl-12 pr-4 py-3.5 bg-white border border-[#E4E4E7] rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20 focus:border-[#1B4332]/40 transition-all placeholder:text-[#A1A1AA]"
            />
          </div>
        </section>

        {/* Error banner */}
        {error && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-center justify-between" data-testid="clientes-error">
            <span>{error}</span>
            <button onClick={() => fetchPage(page, busca.trim(), statusFilter)} className="text-red-700 underline">Tentar novamente</button>
          </div>
        )}

        {/* Client List */}
        <section className="animate-fade-in-up opacity-0" style={{ animationFillMode: 'forwards', animationDelay: '300ms' }}>
          {loading ? (
            <div className="flex items-center justify-center py-20" data-testid="clientes-loading">
              <div className="w-8 h-8 border-2 border-[#1B4332] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : clientesVisiveis.length === 0 ? (
            <div className="salonup-card p-10 md:p-14 text-center max-w-md mx-auto">
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: '#1B433210' }}>
                  <Users size={32} weight="light" style={{ color: '#1B4332' }} />
                </div>
                <h2 className="salonup-heading-md mb-2">
                  {busca || statusFilter ? 'Nenhuma cliente encontrada' : 'Comece cadastrando'}
                </h2>
                <p className="salonup-text-body text-sm max-w-xs">
                  {busca || statusFilter
                    ? 'Tente ajustar a busca ou o filtro.'
                    : 'Adicione a primeira cliente do salao para comecar a construir o historico.'}
                </p>
                {!busca && !statusFilter && (
                  <button
                    onClick={() => { setEditCliente(null); setShowForm(true); }}
                    className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-white"
                    style={{ backgroundColor: '#1B4332' }}
                  >
                    <Plus size={16} weight="bold" />
                    Nova Cliente
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="clientes-list">
              {clientesVisiveis.map((cliente) => {
                const st = STATUS_CONFIG[cliente.status] || STATUS_CONFIG.novo;
                return (
                  <div
                    key={cliente.id}
                    data-testid={`cliente-card-${cliente.id}`}
                    onClick={() => navigate(`/gestao/clientes/${cliente.id}`)}
                    className="salonup-card p-4 md:p-5 flex items-center gap-4 group"
                  >
                    {/* Avatar */}
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-white text-lg font-semibold"
                      style={{ backgroundColor: st.color, fontFamily: 'Outfit' }}
                    >
                      {cliente.nome?.charAt(0)?.toUpperCase() || '?'}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <h3 className="text-[15px] font-medium text-[#18181B] truncate" style={{ fontFamily: 'Outfit' }}>
                          {cliente.nome}
                        </h3>
                        <span
                          className="flex-shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-md uppercase tracking-wide"
                          style={{ backgroundColor: st.bg, color: st.color }}
                        >
                          {st.label}
                        </span>
                        {cliente.alergias && (
                          <span
                            data-testid={`alerta-alergia-${cliente.id}`}
                            className="relative flex-shrink-0 group/tip"
                          >
                            <Warning size={15} weight="fill" className="text-red-400" />
                            <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-[#18181B] text-white text-[11px] font-medium rounded-lg whitespace-nowrap opacity-0 group-hover/tip:opacity-100 transition-opacity duration-200 shadow-lg z-20">
                              Alerta tecnico: alergias registradas
                              <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#18181B]" />
                            </span>
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-[#71717A]" data-testid={`cliente-meta-${cliente.id}`}>
                        {cliente.codigo_cliente && (
                          <span className="font-mono text-[11px] text-[#52525B]" data-testid={`cliente-codigo-${cliente.id}`}>
                            {cliente.codigo_cliente}
                          </span>
                        )}
                        {cliente.telefone && (
                          <span className="flex items-center gap-1">
                            <Phone size={12} weight="regular" />
                            {formatPhone(cliente.telefone)}
                          </span>
                        )}
                        {cliente.ultima_visita && (
                          <span className="flex items-center gap-1">
                            <CalendarBlank size={12} weight="regular" />
                            {formatDateRelative(cliente.ultima_visita)}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="hidden md:flex items-center gap-6 flex-shrink-0 mr-2">
                      <div className="text-center">
                        <p className="text-lg font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>{cliente.total_visitas}</p>
                        <p className="text-[10px] text-[#A1A1AA] uppercase tracking-wide">visitas</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>{formatCurrency(cliente.ticket_medio)}</p>
                        <p className="text-[10px] text-[#A1A1AA] uppercase tracking-wide">ticket</p>
                      </div>
                      {cliente.profissional_top && (
                        <div className="text-center max-w-[100px]">
                          <p className="text-sm font-medium text-[#18181B] truncate" style={{ fontFamily: 'Outfit' }}>{cliente.profissional_top}</p>
                          <p className="text-[10px] text-[#A1A1AA] uppercase tracking-wide">profissional</p>
                        </div>
                      )}
                    </div>

                    {/* Arrow */}
                    <ArrowRight
                      size={18}
                      weight="bold"
                      className="salonup-arrow flex-shrink-0"
                    />
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Paginacao + Total */}
        {!loading && total > 0 && (
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3" data-testid="clientes-pagination">
            <p className="salonup-text-xs" data-testid="clientes-range">
              Exibindo {rangeInicio.toLocaleString('pt-BR')}&ndash;{rangeFim.toLocaleString('pt-BR')} de {total.toLocaleString('pt-BR')} cliente{total !== 1 ? 's' : ''}
            </p>
            <div className="flex items-center gap-2">
              <button
                data-testid="btn-prev-page"
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-medium border border-[#E4E4E7] text-[#71717A] hover:bg-white hover:text-[#18181B] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <CaretLeft size={12} weight="bold" /> Anterior
              </button>
              <span className="text-[11px] text-[#71717A]" data-testid="clientes-page-indicator">
                Pagina <b className="text-[#18181B]">{page}</b> de <b className="text-[#18181B]">{totalPages || 1}</b>
              </span>
              <button
                data-testid="btn-next-page"
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-medium border border-[#E4E4E7] text-[#71717A] hover:bg-white hover:text-[#18181B] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Proxima <CaretRight size={12} weight="bold" />
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="salonup-footer animate-fade-in opacity-0" style={{ animationDelay: '500ms', animationFillMode: 'forwards' }}>
          <p className="salonup-text-xs">SalonUp — O sistema operacional do salao moderno</p>
          <p className="salonup-text-xs">Gestao (CORE) &middot; Clientes</p>
        </footer>
      </div>

      {/* Modal */}
      {showForm && (
        <ClienteFormModal
          cliente={editCliente}
          onClose={() => { setShowForm(false); setEditCliente(null); }}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
};

export default ClientesList;
